<template>
    <layout>
        <template v-slot:header>
            <AppHead title="Ship Orders" />
            <h2>Ship Orders</h2>
            <flash-messages />
        </template>

        <template v-slot:breadcrumbs>
            <BreadCrumbs activeItem="Orders" :crumbs="{ home: 'Dashboard' }" />
        </template>

        <template v-slot:default>
            <section class="mt-3 mb-5 admin-shipments">
                <h4><i class="fas fa-truck-loading text-primary mr-2"></i> {{ userList.total }} Users With Open Orders
                    <button class="btn btn-outline-primary" @click="expandAll">Expand All</button>
                    <button class="btn btn-outline-primary" @click="collapseAll">Collapse All</button>
                </h4>

                <div class="d-flex align-items-center py-1">
                    <div class="mr-2">Order Tags</div>
                    <div class="flex-grow-1">
                        <Multiselect placeholder="All Tags" v-model="searchTags" mode="tags" :options="tags" searchable />
                    </div>
                </div>

                <div class="d-flex align-items-center py-1">
                    <div class="mr-2">Exclude Order Tags</div>
                    <div class="flex-grow-1">
                        <Multiselect v-model="excludeTags" mode="tags" :options="tags" searchable />
                    </div>
                </div>

                <div class="table-responsive">

                    <DataTable ref="admin-shipments-table" :value="userList.data" :paginator="true" :lazy="true"
                        :loading="loadings.users" :first="(userList.current_page - 1) * userListPerPage"
                        :rows="userListPerPage" :totalRecords="userList.total" @page="onPage($event)" @sort="onSort($event)"
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        paginatorPosition="both" :rowsPerPageOptions="[20, 50, 100]"
                        currentPageReportTemplate="({first} - {last} of {totalRecords})"
                        v-model:expandedRows="expandedUsers" sortField="last_order_date" :sortOrder="-1"
                        class="p-datatable-sm admin-datatable" tableClass="border-left border-right border-bottom"
                        dataKey="id">

                        <Column class="expander" :expander="true" headerStyle="width: 3em" />

                        <Column field="company_name" header="Company" :sortable="true">
                            <template #body="slotProps">
                                <inertia-link class="m-text-nowrap"
                                    :href="route('admin.orders.usership', slotProps.data.id)">
                                    {{ slotProps.data.company_name }}
                                </inertia-link>
                            </template>
                        </Column>
                        <Column field="name" header="Name" :sortable="true">
                            <template #body="slotProps">
                                <inertia-link class="m-text-nowrap"
                                    :href="route('admin.orders.usership', slotProps.data.id)">
                                    {{ slotProps.data.name }}
                                </inertia-link>
                            </template>
                        </Column>

                        <Column field="last_order_date" header="Last Order" :sortable="true">
                            <template #body="slotProps">
                                {{ moment(slotProps.data.last_order_date).format('M/D/YY h:mm A') }}
                            </template>
                        </Column>

                        <Column field="oldest_order_date" header="Oldest Order" :sortable="true">
                            <template #body="slotProps">
                                {{ moment(slotProps.data.oldest_order_date).format('M/D/YY h:mm A') }}
                            </template>
                        </Column>

                        <Column field="actionable" header="Ready">
                            <template #body="slotProps">
                                <div class="d-inline pr-5 m-text-nowrap"
                                    :class="slotProps.data.actionable > 0 ? 'status status-ok' : ''">
                                    {{ slotProps.data.actionable }}
                                    Products ready to ship
                                </div>
                            </template>
                        </Column>

                        <template #expansion="slotProps">
                            <table class="table table-striped">
                                <thead>
                                    <tr>
                                        <th>Order #</th>
                                        <th>SKU</th>
                                        <th>Name</th>
                                        <th>Info</th>
                                        <th>Remaining</th>
                                        <th>Inventory</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <template v-for="item in pendingItems(slotProps.data.orderItems)" :key="item.id">
                                        <tr v-if="item.qty_shipping > 0">
                                            <td>
                                                <a :href="route('admin.order.view', item.order_id)" target="_new">{{
                                                    item.order_id + ' ' + item.order_address }}</a>
                                                <OrderItemPaymentIntent class="ml-2" :orderItem="item" />
                                                <Tags :tags="item.order_tags"></Tags>
                                                <DateTimeText v-model="item.order_future_date" prepend="Future Fulfill"
                                                    class="badge badge-info text-white ml-1 p-1"></DateTimeText>
                                            </td>
                                            <td>
                                                <a :href="route('admin.products.view', item.product_id)" target="_new">{{
                                                    item.product_sku }}</a>
                                                <div v-if="item.from_bundle">
                                                    <strong>From Bundle: {{ item.from_bundle.sku }}</strong>
                                                </div>
                                            </td>
                                            <td>{{ item.product_name }}</td>
                                            <td>{{ item.shippedDesc }}</td>
                                            <td><span :class="getStatus(item)">{{ item.qty_shipping }}</span></td>
                                            <td>
                                                <p class="m-0">
                                                    <InventoryInfo :inventoryInfo="item.inventoryInfo" />
                                                </p>
                                            </td>
                                            <td>
                                                <button class="btn btn-outline-primary"
                                                    @click="createDropship(item, slotProps.data.id)"
                                                    v-if="item.dropship">Dropship
                                                </button>
                                                <button class="btn btn-outline-primary"
                                                    @click="createBackorder(item, slotProps.data.id)"
                                                    v-if="!item.dropship && item.inventoryInfo.current_inventory == 0">Backorder
                                                </button>
                                            </td>
                                        </tr>
                                    </template>
                                </tbody>
                            </table>
                        </template>
                    </DataTable>
                </div>
            </section>

            <card class="mt-3" title="Needs Attention" collapse @collapse="onOpenNeedAttention($event)">
                <div class="text-center" v-if="loadings.needsAttention"><i
                        class="fas fa-circle-notch text-primary fa-2x fa-spin"></i></div>
                <div v-for="shipment in needsAttention" :key="shipment.id"
                    :class="{ 'mb-2 pb-2 border-bottom': needsAttention.length > 1 }">
                    <inertia-link class="m-text-nowrap" :href="route('admin.orders.usership', shipment.user.id)">#{{
                        shipment.id
                    }}
                    </inertia-link>
                    <div class="mb-3" v-if="shipment.user.mtd_info">
                        <p class="mb-0 font-weight-bold">Tool User under MTD:</p>
                        <p class="mb-0 text-secondary">
                            <inertia-link :href="route('mtd.view', shipment.user.mtd_info.id)">{{
                                shipment.user.mtd_info.company_name
                            }}
                            </inertia-link>
                            -
                            <Phone v-model="shipment.user.mtd_info.phone_number" />
                        </p>
                    </div>
                    <div v-else>
                        <p class="mb-0 font-weight-bold">Company</p>
                        <p class="mb-0 text-secondary">
                            <inertia-link :href="route('mtd.view', shipment.user.id)">{{ shipment.user.company_name
                            }}</inertia-link>
                        </p>
                        <p class="mb-0 font-weight-bold">User Phone Number:</p>
                        <p class="mb-0 text-secondary">
                            <Phone v-model="shipment.user.phone_number" />
                        </p>
                    </div>
                    <div class="alert alert-danger">
                        Shipment {{ shipment.status_desc }} but has no new tracking events in last two days
                        <TrackingLink :shipment="shipment" />
                    </div>
                    <p class="mb-0">Status: <span class="text-secondary">{{ shipment.status_desc }}</span></p>
                    <p class="mb-0">Last Event: <span class="text-secondary">{{
                        moment(shipment.last_event).format('M/D/YY h:mm A')
                    }}</span></p>
                    <ul class="list-unstyled">
                        <li v-for="item in shipment.items" :key="item.id" class="text-secondary ml-2">{{ item.sku }} {{
                            item.name }}
                            x {{ item.qty }}
                        </li>
                    </ul>
                </div>
            </card>

            <card class="mt-3" title="Manual Shipments" collapse @collapse="onOpenManualShipments($event)">
                <div class="text-center" v-if="loadings.manualShipments"><i
                        class="fas fa-circle-notch text-primary fa-2x fa-spin"></i></div>
                <div v-for="shipment in manualShipments" :key="shipment.id"
                    :class="{ 'mb-2 pb-2 border-bottom': manualShipments.length > 1 }">
                    <div class="mb-3" v-if="shipment.user.mtd_info">
                        <p class="mb-0 font-weight-bold">Tool User under MTD:</p>
                        <p class="mb-0 text-secondary">
                            <inertia-link :href="route('mtd.view', shipment.user.mtd_info.id)">{{
                                shipment.user.mtd_info.company_name
                            }}
                            </inertia-link>
                            -
                            <Phone v-model="shipment.user.mtd_info.phone_number" />
                        </p>
                    </div>
                    <div v-else>
                        <p class="mb-0 font-weight-bold">Company</p>
                        <p class="mb-0 text-secondary">
                            <inertia-link :href="route('mtd.view', shipment.user.id)">{{ shipment.user.company_name
                            }}</inertia-link>
                        </p>
                        <p class="mb-0 font-weight-bold">User Phone Number:</p>
                        <p class="mb-0 text-secondary">
                            <Phone v-model="shipment.user.phone_number" />
                        </p>
                    </div>

                    <ul class="list-unstyled">
                        <li v-for="item in shipment.items" :key="item.id" class="text-secondary ml-2">{{ item.sku }} {{
                            item.name }}
                            x {{ item.qty }}
                        </li>
                    </ul>
                    <p class="mb-0">Prepared at: <span class="text-secondary">
                        {{ moment(shipment.created_at).format('M/D/YY h: mm A') }}</span>
                    </p>
                    <p class="mb-0">Shipped at: <span class="text-secondary">{{
                        moment(shipment.shipped_at).format('M/D/YY h:mm A')
                                                }}</span></p>
                    <p class="mb-0">Status: <span class="text-secondary">{{ shipment.status_desc }}</span></p>

                    <inertia-link :href="route('admin.user.shipment.delivered', shipment)" preserve-scroll>Mark Delivered
                    </inertia-link>
                    <br />
                </div>
            </card>
            <card class="mt-3" title="Dropships" collapse @collapse="onOpenDropships($event)">
                <div class="text-center" v-if="loadings.dropships"><i
                        class="fas fa-circle-notch text-primary fa-2x fa-spin"></i></div>
                <div v-for="shipment in dropships" :key="shipment.id"
                    :class="{ 'mb-2 pb-2 border-bottom': dropships.length > 1 }">
                    <div class="mb-3" v-if="shipment.user.mtd_info">
                        <p class="mb-0 font-weight-bold">Tool User {{ shipment.user.first_name }} {{ shipment.user.last_name
                        }}
                            under MTD:</p>
                        <p class="mb-0 text-secondary">
                            <inertia-link :href="route('mtd.view', shipment.user.mtd_info.id)">{{
                                shipment.user.mtd_info.company_name
                            }}
                            </inertia-link>
                            -
                            <Phone v-model="shipment.user.mtd_info.phone_number" />
                        </p>
                    </div>
                    <div v-else>
                        <p class="mb-0 font-weight-bold">Company</p>
                        <p class="mb-0 text-secondary">
                            <inertia-link :href="route('mtd.view', shipment.user.id)">{{ shipment.user.company_name
                            }}</inertia-link>
                        </p>
                        <p class="mb-0 font-weight-bold">User Phone Number:</p>
                        <p class="mb-0 text-secondary">
                            <Phone v-model="shipment.user.phone_number" />
                        </p>
                    </div>

                    <p class="mb-0">Dropship by: <span class="text-secondary">{{ shipment.dropship_manufacturer_name
                    }}</span></p>
                    <ul class="list-unstyled">
                        <li v-for="item in shipment.items" :key="item.id" class="text-secondary ml-2">{{ item.sku }} {{
                            item.name }}
                            x {{ item.qty }}
                        </li>
                    </ul>
                    <p class="mb-0">Prepared at: <span class="text-secondary">
                        {{ moment(shipment.created_at).format('M/D/YY h: mm A') }}
                        </span>
                    </p>
                    <p class="mb-0">Shipped at: <span class="text-secondary">{{
                        moment(shipment.shipped_at).format('M/D/YY h:mm A')
                                                }}</span></p>
                    <p class="mb-0">Status: <span class="text-secondary">{{ shipment.status_desc }}</span></p>

                    <div v-if="shipment.status == 1 || shipment.status == 2">
                        <inertia-link :href="route('admin.orders.shipment.cancel', shipment)" preserve-scroll>Cancel
                            Shipment
                        </inertia-link>
                        <br />
                        <inertia-link :href="route('admin.orders.shipment.shipped', shipment)" preserve-scroll>Mark Shipped
                        </inertia-link>
                        <br />
                    </div>
                    <div v-else-if="shipment.status == 6">
                        <inertia-link :href="route('admin.orders.shipment.delivered', shipment)" preserve-scroll>Mark
                            Delivered
                        </inertia-link>
                        <br />
                    </div>
                </div>
            </card>


        </template>
    </layout>
</template>

<script>
import Icon from '@/Shared/Icon'
import Layout from '@/Shared/Admin/Layout'
import SearchFilter from '@/Shared/SearchFilter'
import FlashMessages from '@/Shared/FlashMessages'
import moment from 'moment'
import BreadCrumbs from '@/Shared/BreadCrumbs'
import Tags from '@/Components/General/Tags'
import OrderItemPaymentIntent from '@/Components/General/OrderItemPaymentIntent'
import Multiselect from '@vueform/multiselect/src/Multiselect'
import TrackingLink from '@/Components/General/TrackingLink.vue'
import InventoryInfo from '@/Components/General/InventoryInfo.vue'

export default {
    components: {
        Icon,
        SearchFilter,
        Layout,
        FlashMessages,
        BreadCrumbs,
        Tags,
        OrderItemPaymentIntent,
        Multiselect,
        TrackingLink,
        InventoryInfo,
    },
    props: {
        filters: Object,
        search: String,
        status: Array,
        selectedStatus: Array,
        userList: {
            type: Object,
            default: {
                data: [],
                per_page: 20,
                current_page: 0,
                total: 0,
            }
        },
        backorder: Array,
        manualShipments: Array,
        dropships: Array,
        needsAttention: Array,
        selectedTags: Array,
        selectedExcludeTags: Array,
        tags: Array,
    },
    data() {
        return {
            form: this.$inertia.form({
                box_size: null,
                weight: null,
                qty: null,
                shipping_method_id: null,
                parent_shipment_id: null,
                items: null,
                backorder_eta: null
            }),
            searchText: this.search,
            searchStatus: this.selectedStatus,
            expandedUsers: [],
            searchTags: (this.selectedTags || []).map(t => t.id),
            excludeTags: (this.selectedExcludeTags || []).map(t => t.id),
            loadings: {
                users: true,
                needsAttention: true,
                manualShipments: true,
                dropships: true,
            },
        }
    },
    created() {
        this.moment = moment
    },
    mounted() {
        this.userList.per_page = this.query?.get('per_page') || this.userList.per_page || 20
        this.getShips()
    },
    computed: {
        userListPerPage() {
            return parseInt(this.userList?.per_page || 0)
        }
    },
    watch: {
        excludeTags() {
            this.getShips({ page: 1 });
        },
        searchTags() {
            this.getShips({ page: 1 });
        }
    },
    methods: {
        pendingItems(items) {
            return items.filter(item => item.qty_shipping > 0)
                .map(item => {
                    if (item.order_future_date) {
                        const date = new Date(item.order_future_date)
                        item.tmp_order_future_date = new Date(date.getFullYear(), date.getMonth(), date.getDate() - 7);
                    } else {
                        item.tmp_order_future_date = new Date();
                    }
                    return item
                })
                .sort((a, b) => {
                    return a.tmp_order_future_date - b.tmp_order_future_date
                })
        },
        getShips(query) {
            this.loadings.users = true
            query = query || {}
            query.tags = this.searchTags
            query.exclude_tags = this.excludeTags

            this.$inertia.post(route('admin.orders.ship', this.mergeQueryParams({
                per_page: this.userList.per_page, ...query,
            })), {}, {
                preserveScroll: true,
                only: ['userList'],
                onSuccess: () => {
                    this.loadings.users = false
                },
                onError: () => {
                    this.loadings.users = false
                },
            });
        },
        onPage(e) {
            this.getShips({ page: e.page + 1, per_page: e.rows })
        },
        onSort(e) {
            this.getShips({ sort_field: e.sortField, sort_order: e.sortOrder })
        },
        expandAll() {
            this.expandedUsers = this.userList.data;
        },
        collapseAll() {
            this.expandedUsers = [];
        },
        search() {
            this.$refs['admin-orders-table'].resetPage()
            this.$inertia.get(this.route('admin.orders'), {
                search: this.searchText,
                status: this.searchStatus
            })
        },
        getStatus(item) {
            if (item.qty_shipping <= item.inventoryInfo.current_inventory && item.qty_shipping >= item.inventoryInfo.outgoing_inventory) return 'status status-ok'
            if (item.inventoryInfo.current_inventory > 0) return 'status status-warning'
        },

        createDropship(item, userId) {
            let items = [{
                id: item.id,
                qty: item.qty_shipping
            }];
            this.form.items = JSON.stringify(items);

            this.form.post(this.route('orders.createshipmentdropship', userId), {
                preserveScroll: true
            })
        },
        createBackorder(item, userId) {
            let items = [{
                id: item.id,
                qty: item.qty_shipping
            }];

            this.form.items = JSON.stringify(items);
            this.form.post(this.route('orders.createshipmentbackorder', userId), {
                preserveScroll: true
            })
        },

        onOpenNeedAttention(collapsed) {
            if (!collapsed) {
                this.loadings.needsAttention = true
                this.$inertia.post(route('admin.orders.ship', this.mergeQueryParams()), {}, {
                    preserveScroll: true,
                    only: ['needsAttention'],
                    onSuccess: () => {
                        this.loadings.needsAttention = false
                    },
                    onError: () => {
                        this.loadings.needsAttention = false
                    },
                });
            }
        },
        onOpenManualShipments(collapsed) {
            if (!collapsed) {
                this.loadings.manualShipments = true
                this.$inertia.post(route('admin.orders.ship', this.mergeQueryParams()), {}, {
                    preserveScroll: true,
                    only: ['manualShipments'],
                    onSuccess: () => {
                        this.loadings.manualShipments = false
                    },
                    onError: () => {
                        this.loadings.manualShipments = false
                    },
                });
            }
        },
        onOpenDropships(collapsed) {
            if (!collapsed) {
                this.loadings.dropships = true
                this.$inertia.post(route('admin.orders.ship', this.mergeQueryParams()), {}, {
                    preserveScroll: true,
                    only: ['dropships'],
                    onSuccess: () => {
                        this.loadings.dropships = false
                    },
                    onError: () => {
                        this.loadings.dropships = false
                    },
                });
            }
        },
    },
}
</script>

<style>
.status-option-count {
    float: right;
    margin-left: 15px;
    width: 40px;
    background-color: #e8e8e8;
    color: #000;
    border-radius: 5px;
}

tr.has-actions {
    background-color: #66FF99;
    font-weight: bold;
}
</style>
<style src="@vueform/multiselect/themes/default.css"></style>
<style>
.admin-shipments .multiselect {
    min-height: unset;
    height: calc(1.5em + 0.5rem + 2px);
    font-size: 0.7875rem;
    padding: 1rem 0;
}
</style>
